import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SchoolIcon from '@mui/icons-material/School';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { trilhaJson } from './api';

export default function TrilhaSetoresPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [setores, setSetores] = useState([]);
  const [me, setMe] = useState({ canManageSetores: false });
  const [busca, setBusca] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [responsaveis, setResponsaveis] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, perms] = await Promise.all([
        trilhaJson('/api/trilha-aprendizado/setores'),
        trilhaJson('/api/trilha-aprendizado/me'),
      ]);
      setSetores(Array.isArray(list) ? list : []);
      setMe(perms || {});
    } catch (e) {
      setError(e.message || 'Erro ao carregar setores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(withUnidade('/api/usuarios', unidadeSelecionada));
        const data = await res.json().catch(() => []);
        if (!cancelled) {
          const arr = Array.isArray(data) ? data : data.usuarios || data.items || [];
          setUsuarios(arr.filter((u) => u?.id && (u.nome || u.email)));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unidadeSelecionada]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return setores;
    return setores.filter((s) => String(s.nome || '').toLowerCase().includes(q));
  }, [setores, busca]);

  const nomeDuplicado = useMemo(() => {
    const n = nome.trim().toLowerCase();
    if (!n) return false;
    return setores.some(
      (s) => String(s.nome || '').toLowerCase() === n && (!editing || s.id !== editing.id)
    );
  }, [nome, setores, editing]);

  const openCreate = () => {
    setEditing(null);
    setNome('');
    setDescricao('');
    setResponsaveis([]);
    setOpen(true);
  };

  const openEdit = (setor) => {
    setEditing(setor);
    setNome(setor.nome || '');
    setDescricao(setor.descricao || '');
    const ids = new Set(setor.responsaveisIds || []);
    setResponsaveis(usuarios.filter((u) => ids.has(Number(u.id))));
    setOpen(true);
  };

  const handleSave = async () => {
    if (!nome.trim() || nomeDuplicado) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        responsaveisIds: responsaveis.map((u) => Number(u.id)),
      };
      if (editing) {
        await trilhaJson(`/api/trilha-aprendizado/setores/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        await trilhaJson('/api/trilha-aprendizado/setores', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      setOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (setor) => {
    if (!window.confirm(`Excluir o setor "${setor.nome}"?`)) return;
    try {
      await trilhaJson(`/api/trilha-aprendizado/setores/${setor.id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#00695c' }}>
            Trilha de Aprendizado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Conteúdo institucional — visível para todas as unidades
          </Typography>
        </Box>
        {me.canManageSetores && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#00897b' }}>
            Novo Setor
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Buscar setor..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        sx={{ mb: 2, maxWidth: 360, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 2 }}
        fullWidth
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#00897b' }} />
        </Box>
      ) : filtrados.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <SchoolIcon sx={{ fontSize: 56, color: '#80cbc4', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum setor ainda
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {me.canManageSetores
              ? 'Crie o primeiro setor para organizar a trilha de aprendizado.'
              : 'Aguarde a criação de setores pelo Setor de Contratos.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtrados.map((setor) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={setor.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(0,137,123,0.12)',
                  bgcolor: 'rgba(255,255,255,0.92)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(0,137,123,0.15)' },
                }}
              >
                <CardActionArea onClick={() => navigate(`/trilha-aprendizado/setores/${setor.id}`)}>
                  <CardContent>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #00897b 0%, #26a69a 100%)',
                        color: '#fff',
                      }}
                    >
                      <SchoolIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} noWrap>
                      {setor.nome}
                    </Typography>
                    {setor.descricao && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                        {setor.descricao}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={`${setor.pastasCount || 0} pasta(s)`}
                      sx={{ mt: 1.5 }}
                    />
                  </CardContent>
                </CardActionArea>
                {me.canManageSetores && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1, gap: 0.5 }}>
                    <IconButton size="small" onClick={() => openEdit(setor)} aria-label="Editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(setor)} aria-label="Excluir" color="error">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar setor' : 'Novo setor'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Nome do setor"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={nomeDuplicado}
            helperText={nomeDuplicado ? 'Já existe um setor com este nome.' : ' '}
            fullWidth
            autoFocus
          />
          <TextField
            label="Descrição (opcional)"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Autocomplete
            multiple
            options={usuarios}
            value={responsaveis}
            onChange={(_, v) => setResponsaveis(v)}
            getOptionLabel={(u) => u.nome || u.email || String(u.id)}
            isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
            renderInput={(params) => (
              <TextField {...params} label="Responsáveis" placeholder="Buscar usuário..." />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !nome.trim() || nomeDuplicado}
            sx={{ bgcolor: '#00897b' }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
